import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend);

const CHART_CONFIG = {
  type: 'line',
  data: {},
  options: {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#ffffff',
        titleColor: '#6B778F',
        bodyColor: '#3F495A',
        borderColor: 'rgba(27, 36, 50, 0.1)',
        borderWidth: 1,
        cornerRadius: 4,
        displayColors: false,
        titleFont: {
          family: 'Silka, Helvetica, Arial, sans-serif',
          size: 12,
          weight: '400',
        },
        bodyFont: {
          family: 'Silka, Helvetica, Arial, sans-serif',
          size: 12,
          weight: '400',
        },
        padding: 8,
        boxShadow: '0px 1px 4px 0px rgba(27, 36, 50, 0.06), 0px 2px 32px 0px rgba(27, 36, 50, 0.06)',
        callbacks: {
          title(context) {
            return context[0].dataset.label;
          },
          label(context) {
            return `${context.label}: ${' '.repeat(15)} ${context.raw}`;
          },
        },
      },
    },
    elements: {
      line: {
        fill: false,
        borderWidth: 3,
        borderColor: '#4900ff',
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        hoverBorderWidth: 3,
        borderWidth: 2,
        borderColor: '#4900ff',
        backgroundColor: '#fff',
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          color: '#777d88',
          font: {
            family: 'Silka, Helvetica, Arial, sans-serif',
          },
          autoSkipPadding: 24,
        },
      },
      y: {
        grid: {
          color: '#e4e4e7',
        },
        ticks: {
          beginAtZero: true,
          precision: 0,
          color: '#777d88',
          font: {
            family: 'Silka, Helvetica, Arial, sans-serif',
          },
        },
      },
    },
  },
};

const Chart = ({ labels, datasets }) => {
  const inputEl = React.useRef();
  const chartsjsObject = React.useRef();

  const updateChart = () => {
    CHART_CONFIG.data.labels = labels;
    CHART_CONFIG.data.datasets = datasets;

    if (chartsjsObject.current) {
      chartsjsObject.current.update();
    } else {
      const ctx = inputEl.current.getContext('2d');
      chartsjsObject.current = new ChartJS(ctx, CHART_CONFIG);
    }
  };

  // Update chart on props update
  useEffect(() => {
    updateChart();
    // eslint-disable-next-line
  }, [labels, datasets]);

  // Destroy chart on component unmount
  useEffect(
    () => () => {
      if (chartsjsObject.current) {
        chartsjsObject.current.destroy();
      }
    },
    [],
  );

  return (
    <div style={{ position: 'relative', height: 280 }}>
      <canvas ref={inputEl}></canvas>
    </div>
  );
};

Chart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      data: PropTypes.array,
    }),
  ).isRequired,
};

export default Chart;
